import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, rupee } from "../lib/api";
import { useStores } from "../lib/stores";
import {
  formatSaleDate,
  paymentStatusLabel,
  paymentStatusStyle,
  saleBillAmounts,
} from "../lib/saleInvoice";
import { ChevronRight, Receipt } from "lucide-react";
import { toast } from "sonner";

const GRID_HEAD = "sales-grid-head";
const GRID_ROW = "sales-grid-row";

function PaymentBadge({ status }) {
  const style = paymentStatusStyle(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ background: style.background, color: style.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: style.dot }} />
      {paymentStatusLabel(status)}
    </span>
  );
}

function CustomerCell({ snapshot }) {
  const name = snapshot?.name || "Walk-in";
  const initial = name.charAt(0).toUpperCase();
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="w-9 h-9 rounded-full shrink-0 grid place-items-center text-sm font-semibold"
        style={{ background: "var(--drag-highlight)", color: "var(--primary)" }}
      >
        {initial}
      </div>
      <div className="min-w-0">
        <div className="font-medium truncate leading-tight">{name}</div>
        {snapshot?.phone && (
          <div className="text-xs truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
            {snapshot.phone}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Sales() {
  const [items, setItems] = useState([]);
  const { activeStoreId } = useStores();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const params = activeStoreId ? { store_id: activeStoreId } : {};
    if (searchParams.get("test_mail") === "1") {
      params.test_mail = "1";
      const to = searchParams.get("to");
      if (to) params.to = to;
    }
    api.get("/sales", { params })
      .then((r) => {
        setItems(r.data);
        if (searchParams.get("test_mail") === "1") {
          toast.success(`Test email sent to ${searchParams.get("to") || "your account email"}`);
        }
      })
      .catch((err) => {
        if (searchParams.get("test_mail") === "1") {
          toast.error(err?.response?.data?.detail || "Test email failed");
        }
      });
  }, [activeStoreId, searchParams]);

  const openInvoice = (sale) => navigate(`/app/sales/${sale.id}`);

  const collectPayment = (e, sale) => {
    e.stopPropagation();
    navigate(`/app/sales/${sale.id}?collect=1`);
  };

  return (
    <div className="p-6 md:p-10 max-w-[90rem] mx-auto" data-testid="sales-root">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Sales history</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {items.length === 0 ? "No invoices yet" : `${items.length} invoice${items.length === 1 ? "" : "s"}`}
            {items.length > 0 && " · click a row to view GST invoice"}
          </p>
        </div>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-0">
            <div className={GRID_HEAD} aria-hidden>
              <div>Invoice</div>
              <div>Date</div>
              <div>Customer</div>
              <div>Status</div>
              <div className="sales-col-center">Items</div>
              <div className="sales-col-num">Amount</div>
              <div className="sales-col-num">Discount</div>
              <div className="sales-col-num">Before tax</div>
              <div className="sales-col-num">Tax</div>
              <div className="sales-col-num">After tax</div>
              <div className="sales-col-num">Due</div>
            </div>

            {items.length === 0 && (
              <div className="py-16 text-center">
                <Receipt size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No sales yet</p>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  Completed POS bills will appear here.
                </p>
              </div>
            )}

            {items.map((s) => {
              const hasDue = s.balance_due > 0;
              const hasReturn = (s.returned_total || 0) > 0;
              const bill = saleBillAmounts(s);

              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  className={`${GRID_ROW} group`}
                  onClick={() => openInvoice(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openInvoice(s);
                    }
                  }}
                  data-testid={`sale-row-${s.invoice_no}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-sm font-semibold truncate" style={{ color: "var(--primary)" }}>
                      {s.invoice_no}
                    </span>
                    <ChevronRight
                      size={15}
                      className="shrink-0 opacity-0 -translate-x-1 group-hover:opacity-50 group-hover:translate-x-0 transition-all"
                      style={{ color: "var(--text-muted)" }}
                    />
                  </div>

                  <div className="text-sm whitespace-nowrap">{formatSaleDate(s.created_at)}</div>

                  <CustomerCell snapshot={s.customer_snapshot} />

                  <div>
                    <PaymentBadge status={s.payment_status} />
                    {s.return_status === "partial_return" && (
                      <div className="text-[10px] mt-1 font-semibold uppercase" style={{ color: "#92400e" }}>Partial return</div>
                    )}
                    {s.return_status === "full_return" && (
                      <div className="text-[10px] mt-1 font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Fully returned</div>
                    )}
                  </div>

                  <div className="sales-col-center text-sm num">{s.items.length}</div>

                  <div className="sales-col-num text-sm num">{rupee(bill.amount)}</div>
                  <div className="sales-col-num text-sm num" style={{ color: bill.discount > 0 ? "var(--error)" : "var(--text-muted)" }}>
                    {bill.discount > 0 ? `−${rupee(bill.discount)}` : "—"}
                  </div>
                  <div className="sales-col-num text-sm num">{rupee(bill.beforeTax)}</div>
                  <div className="sales-col-num text-sm num">{rupee(bill.tax)}</div>
                  <div className="sales-col-num text-sm font-semibold num">
                    {hasReturn ? (
                      <span>
                        <span>{rupee(s.net_total ?? bill.afterTax)}</span>
                        <span className="block text-xs line-through font-normal" style={{ color: "var(--text-muted)" }}>
                          {rupee(bill.afterTax)}
                        </span>
                      </span>
                    ) : (
                      rupee(bill.afterTax)
                    )}
                  </div>

                  <div className="sales-col-num">
                    {hasDue ? (
                      <div className="inline-flex items-center justify-end gap-2.5 w-full">
                        <span className="num text-sm font-semibold whitespace-nowrap" style={{ color: "var(--error)" }}>
                          {rupee(s.balance_due)}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => collectPayment(e, s)}
                          className="sales-collect-btn shrink-0 h-8 px-3 rounded-md text-xs font-semibold border tactile-btn whitespace-nowrap"
                          style={{
                            borderColor: "var(--success)",
                            background: "var(--surface-success)",
                            color: "var(--success)",
                          }}
                          data-testid={`sale-collect-${s.invoice_no}`}
                        >
                          Collect
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm" style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
